import { useEffect, useRef, useState } from 'react';
import type { JSX } from 'react';
import type { ModeId } from '@evi/skins';

import { clearProgramCache, releaseCanvasContext } from './glContext.js';
import { FluidSim, type FluidVariantId, type FluidStepInputs } from './sim/fluidSim.js';
import { PhysarumSim, type PhysarumVariantId } from './sim/physarumSim.js';
import { WaveSim, type WaveVariantId } from './sim/waveSim.js';

/**
 * Theme charging sims — host component for the REAL multi-pass
 * simulations (stable-fluids today; physarum joins on the same
 * interface). Sibling of ShaderChargingAnimation and deliberately
 * shaped like it: same prop surface (the dispatcher spreads the
 * same object), same pausable-RAF posture, same context-loss and
 * WebGL2-fallback behaviour, same per-context program cache
 * underneath (via glContext.getOrCreateProgram).
 *
 * What's different, and why it's a separate component:
 *   - Ping-pong framebuffers and multiple passes per frame instead
 *     of one fragment program straight to canvas.
 *   - The pointer is INPUT to the simulation (splats / attraction),
 *     not an impulse uniform — pointer state converts to sim uv
 *     space and feeds every step.
 *   - The sim carries state between frames, which changes the
 *     reduced-motion and first-paint stories: a raw first frame is
 *     an empty black texture, so setup runs a WARMUP (a few dozen
 *     hidden steps with the emitters running) before the first
 *     visible paint. Reduced motion then freezes on that developed
 *     frame — matching the "one themed frame, then stop" convention
 *     without showing an empty void. Inactive picker cards get the
 *     same developed still.
 *
 * Frame pacing: sims run under the 30fps cap unconditionally (the
 * phone is not necessarily plugged in while the CAR charges — same
 * budget rationale as CAPPED_SIM_KINDS on the single-pass side).
 */

interface SimChargingAnimationProps {
  themeCode: string;
  mode: ModeId;
  percent: number;
  powerKw?: number;
  active?: boolean;
  size?: number;
  glow?: 'breathing' | 'subtle' | 'none';
  accentOverride?: string;
  chargingOverride?: string;
  kwhDelivered?: number;
  simKind: 'fluid' | 'physarum' | 'wave';
  simVariant: FluidVariantId | PhysarumVariantId | WaveVariantId;
}

/** What the host needs from any sim — FluidSim and PhysarumSim share
 *  this shape structurally (their step-input records are identical). */
interface ChargingSimInstance {
  step(dt: number, inputs: FluidStepInputs): void;
  render(): void;
  dispose(): void;
}

const SIM_FRAME_CAP_MS = 33;
const SSR_FALLBACK_SIZE = 320;
const MAX_DPR = 2;
const WAVE_SETTLE_MS = 5500;
/** Hidden steps at setup so the first visible frame shows a living
 *  surface (and reduced-motion / inactive cards freeze on structure,
 *  not on black). ~2 simulated seconds at the capped frame rate. */
const WARMUP_STEPS = 60;
const WARMUP_DT = 1 / 30;

const POWER_KW_DIVISOR = 50.0;
const POWER_KW_MIN_INTENSITY = 0.2;
const POWER_KW_MAX_INTENSITY = 1.0;

let WEBGL2_UNSUPPORTED_LOGGED = false;

function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v));
}

function computeCssSize(propsSize: number | undefined): number {
  if (propsSize !== undefined) return propsSize;
  if (typeof window === 'undefined') return SSR_FALLBACK_SIZE;
  return Math.min(window.innerWidth * 0.6, 400);
}

function computeDpr(): number {
  if (typeof window === 'undefined') return 1;
  return Math.min(window.devicePixelRatio ?? 1, MAX_DPR);
}

function stillBackground(variant: SimChargingAnimationProps['simVariant'], mode: ModeId): string {
  const light = mode === 'light';
  if (variant.startsWith('fluid-cartographic')) {
    return `radial-gradient(ellipse at 65% 35%, #cad839, #33864b 42%, ${light ? '#f7f2df' : '#102216'} 75%)`;
  }
  if (variant.startsWith('fluid-midnight')) {
    return `radial-gradient(ellipse at 35% 40%, #64adf0, #3b59ac 38%, ${light ? '#e8f2ff' : '#080e2c'} 78%)`;
  }
  if (variant.startsWith('wave-')) {
    return variant.startsWith('wave-bloom')
      ? `radial-gradient(circle at 50% 55%, #fff0ab, #f0a031 38%, ${light ? '#ffeecf' : '#49152d'} 75%)`
      : `linear-gradient(155deg, ${light ? '#ffdeb4' : '#47284f'}, #ee8540 48%, #853660)`;
  }
  return 'radial-gradient(circle at 50% 45%, var(--evi-charging, #22C55E) 0%, '
    + 'color-mix(in srgb, var(--evi-charging, #22C55E) 30%, transparent) 42%, transparent 72%)';
}

export function SimChargingAnimation(props: SimChargingAnimationProps): JSX.Element {
  const { simKind, simVariant, size } = props;

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const propsRef = useRef(props);
  useEffect(() => {
    propsRef.current = props;
  });

  // Pointer state in sim uv space (0..1, y up). dx/dy accumulate
  // between ticks (several pointermoves can land per capped frame)
  // and are consumed + zeroed by each tick.
  const pointerRef = useRef({ x: 0.5, y: 0.5, dx: 0, dy: 0, down: false, tapPending: false, movedSinceTick: false, settleUntil: 0, id: null as number | null });

  const [webglFailed, setWebglFailed] = useState(false);
  const [contextEpoch, setContextEpoch] = useState(0);

  const reducedMotionRef = useRef(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    reducedMotionRef.current = mql.matches;
    const handler = (e: MediaQueryListEvent) => {
      reducedMotionRef.current = e.matches;
    };
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  // Deterministic WebGL context release on TRUE unmount (route leave).
  // Empty-deps so the cleanup fires ONLY on unmount, never on a
  // simKind/variant/size/epoch re-run (which reuses this same canvas +
  // context — losing it there would black the preview out). Declared
  // BEFORE the context-loss listener effect so those listeners detach
  // first under React's reverse-order cleanup, and loseContext() fires
  // with no handler attached. See glContext.releaseCanvasContext for
  // the context-cap rationale (this is what stops the /themes preview
  // grid from starving the Mapbox map's tiles on return to /map).
  useEffect(() => {
    const canvas = canvasRef.current;
    return () => releaseCanvasContext(canvas);
  }, []);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const onLost = (e: Event): void => {
      e.preventDefault();
    };
    const onRestored = (): void => {
      const gl = canvas.getContext('webgl2');
      if (gl) clearProgramCache(gl);
      setContextEpoch((n) => n + 1);
    };
    canvas.addEventListener('webglcontextlost', onLost);
    canvas.addEventListener('webglcontextrestored', onRestored);
    return () => {
      canvas.removeEventListener('webglcontextlost', onLost);
      canvas.removeEventListener('webglcontextrestored', onRestored);
    };
  }, []);

  const cssSize = computeCssSize(size);
  const dpr = computeDpr();
  const backingSize = Math.round(cssSize * dpr);

  // ── Sim lifecycle ────────────────────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    let glOrNull: WebGL2RenderingContext | null = null;
    try {
      glOrNull = canvas.getContext('webgl2');
    } catch {
      glOrNull = null;
    }
    if (!glOrNull) {
      if (!WEBGL2_UNSUPPORTED_LOGGED) {
        // eslint-disable-next-line no-console
        console.error(
          '[SimChargingAnimation] WebGL2 unavailable; rendering the static '
          + 'themed-gradient fallback.',
        );
        WEBGL2_UNSUPPORTED_LOGGED = true;
      }
      setWebglFailed(true);
      return;
    }
    const gl = glOrNull;

    // Per-instance seed WITHOUT Math.random: mix the mount time with
    // the canvas identity so simultaneous preview tiles desync.
    const seed = (performance.now() * 1000 + backingSize * 7919) & 0x7fffffff;
    const sim: ChargingSimInstance | null = simKind === 'fluid'
      ? FluidSim.create(gl, simVariant as FluidVariantId, backingSize, seed)
      : simKind === 'wave'
        ? WaveSim.create(gl, simVariant as WaveVariantId, backingSize)
        : PhysarumSim.create(gl, simVariant as PhysarumVariantId, backingSize, seed);
    if (!sim) {
      setWebglFailed(true);
      return;
    }

    const stepInputs = (timeSec: number): FluidStepInputs => {
      const ptr = pointerRef.current;
      const inputs: FluidStepInputs = {
        pointer: {
          x: ptr.x,
          y: ptr.y,
          dx: ptr.dx,
          dy: ptr.dy,
          down: ptr.down || ptr.tapPending,
          moved: ptr.movedSinceTick,
        },
        intensity: clamp(
          (propsRef.current.powerKw ?? 0) / POWER_KW_DIVISOR,
          POWER_KW_MIN_INTENSITY,
          POWER_KW_MAX_INTENSITY,
        ),
        active: propsRef.current.active !== false,
        timeSec,
      };
      ptr.dx = 0;
      ptr.dy = 0;
      ptr.movedSinceTick = false;
      ptr.tapPending = false;
      return inputs;
    };

    // Warmup: develop the sim before anyone sees it. Uses synthetic
    // time starting at 0; the live clock below continues from here.
    let simClock = 0;
    for (let i = 0; i < WARMUP_STEPS; i += 1) {
      sim.step(WARMUP_DT, {
        pointer: { x: 0.5, y: 0.5, dx: 0, dy: 0, down: false, moved: false },
        intensity: POWER_KW_MIN_INTENSITY,
        active: true,
        timeSec: simClock,
      });
      simClock += WARMUP_DT;
    }
    sim.render();

    let rafId: number | null = null;
    let lastTickAt = 0;
    let lastFrameMs: number | null = null;
    let disposed = false;

    const tick = (): void => {
      // Frozen states hold the developed warmup frame: reduced motion
      // always; inactive surfaces (picker cards for non-applied
      // themes) unless the user is actively touching them.
      const frozen = reducedMotionRef.current
        || (propsRef.current.active === false && !pointerRef.current.down && !pointerRef.current.tapPending
          && performance.now() > pointerRef.current.settleUntil);
      if (!frozen) {
        const nowMs = performance.now();
        const dt = lastFrameMs === null
          ? WARMUP_DT
          : Math.min(0.05, (nowMs - lastFrameMs) / 1000);
        lastFrameMs = nowMs;
        simClock += dt;
        sim.step(dt, stepInputs(simClock));
        sim.render();
      } else {
        // Keep the frame clock from accumulating a giant dt across a
        // frozen stretch.
        lastFrameMs = null;
      }
    };

    const scheduleNext = (): void => {
      rafId = requestAnimationFrame(() => {
        const now = performance.now();
        if (now - lastTickAt >= SIM_FRAME_CAP_MS - 1) {
          lastTickAt = now;
          tick();
        }
        if (!disposed) scheduleNext();
      });
    };
    const handleVisibility = (): void => {
      if (document.hidden) {
        if (rafId !== null) {
          cancelAnimationFrame(rafId);
          rafId = null;
        }
        lastFrameMs = null;
      } else if (rafId === null && !disposed) {
        scheduleNext();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    if (!document.hidden) scheduleNext();

    return () => {
      disposed = true;
      document.removeEventListener('visibilitychange', handleVisibility);
      if (rafId !== null) cancelAnimationFrame(rafId);
      sim.dispose();
    };
  }, [simKind, simVariant, backingSize, contextEpoch]);

  // ── Pointer capture → sim uv space ───────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ptr = pointerRef.current;
    const toUv = (e: PointerEvent): { x: number; y: number } => {
      const rect = canvas.getBoundingClientRect();
      return {
        x: clamp((e.clientX - rect.left) / rect.width, 0, 1),
        // GL v-axis points up; client y points down.
        y: clamp(1 - (e.clientY - rect.top) / rect.height, 0, 1),
      };
    };
    const onDown = (e: PointerEvent): void => {
      if(ptr.id!==null || (e.pointerType==='mouse' && e.button!==0)) return;
      const p = toUv(e);
      ptr.id=e.pointerId;
      ptr.down = true;
      ptr.tapPending = true;
      ptr.x = p.x;
      ptr.y = p.y;
      ptr.dx = 0;
      ptr.dy = 0;
      // A stationary tap injects energy as well as a drag. This fixes the
      // existing Initiative/Electric fluid tap, which used to do nothing.
      ptr.movedSinceTick=true;
      if (propsRef.current.simKind === 'wave') ptr.settleUntil=performance.now()+WAVE_SETTLE_MS;
      try { canvas.setPointerCapture(e.pointerId); } catch { /* unsupported — ignore */ }
    };
    const onMove = (e: PointerEvent): void => {
      if (!ptr.down || ptr.id!==e.pointerId) return;
      const p = toUv(e);
      ptr.dx += p.x - ptr.x;
      ptr.dy += p.y - ptr.y;
      ptr.x = p.x;
      ptr.y = p.y;
      ptr.movedSinceTick = true;
      if (propsRef.current.simKind === 'wave') ptr.settleUntil=performance.now()+WAVE_SETTLE_MS;
    };
    const onUp = (e: PointerEvent): void => {
      if(ptr.id!==e.pointerId) return;
      ptr.down = false;
      ptr.id=null;
      if (propsRef.current.simKind === 'wave') ptr.settleUntil=performance.now()+WAVE_SETTLE_MS;
      try { canvas.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
    };
    canvas.addEventListener('pointerdown', onDown);
    canvas.addEventListener('pointermove', onMove);
    canvas.addEventListener('pointerup', onUp);
    canvas.addEventListener('pointercancel', onUp);
    canvas.addEventListener('lostpointercapture', onUp);
    return () => {
      canvas.removeEventListener('pointerdown', onDown);
      canvas.removeEventListener('pointermove', onMove);
      canvas.removeEventListener('pointerup', onUp);
      canvas.removeEventListener('pointercancel', onUp);
      canvas.removeEventListener('lostpointercapture', onUp);
    };
  }, []);

  if (webglFailed) {
    return (
      <div
        aria-hidden
        style={{
          width: `${cssSize}px`,
          height: `${cssSize}px`,
          borderRadius: simVariant.startsWith('fluid-cartographic')
            || simVariant.startsWith('fluid-midnight') || simVariant.startsWith('wave-') ? 0 : '50%',
          background: stillBackground(simVariant, props.mode),
        }}
      />
    );
  }

  return (
    <canvas
      ref={canvasRef}
      width={backingSize}
      height={backingSize}
      style={{
        width: `${cssSize}px`,
        height: `${cssSize}px`,
        display: 'block',
        // The finger drives the fluid, not the page scroll — same
        // claim the single-pass shader canvas makes.
        touchAction: 'none',
      }}
    />
  );
}
